import React, { useMemo, useState } from 'react';
import * as emailjs from '@emailjs/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faClock, 
  faMapMarkerAlt 
} from '@fortawesome/free-solid-svg-icons';
import ConsentEmbed from '../common/ConsentEmbed';
import '../../styles/ContactPage.css';

const KontaktPage: React.FC = () => {
  const topics = useMemo(() => [
    'Allgemeine Anfrage',
    'Elektrotechnik – Angebot/Projektanfrage',
    'Energietechnik / Photovoltaik',
    'Sicherheitstechnik',
    'IuK / Netzwerktechnik',
    'HLS – Heizung, Lüftung, Sanitär',
    'Architektur / Plan3D',
    'Sonstiges'
  ], []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    topic: 'Allgemeine Anfrage',
    message: '',
    // LP entfernt
    captcha: '',
    dataProtection: false
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // keine LP-Auswahl mehr
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form (alle Pflichtfelder)
    if (!formData.name || !formData.email || !formData.phone || !formData.message || !formData.topic || !formData.captcha) {
      setFormError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    // keine LP-Validierung
    if (formData.captcha.trim() !== '7') {
      setFormError('Bitte beantworten Sie die Testfrage korrekt.');
      return;
    }
    
    if (!formData.dataProtection) {
      setFormError('Bitte akzeptieren Sie die Datenschutzerklärung.');
      return;
    }
    
    try {
      setIsSending(true);
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        topic: formData.topic,
        message: formData.message,
        time: new Date().toLocaleString(),
        reply_to: formData.email,
        // zusätzliche Aliasse für gängige EmailJS-Templates
        from_name: formData.name,
        subject: `Neue Anfrage von ${formData.name} - ${formData.topic}`,
        to_name: 'IB Neubaur'
      };

      await emailjs.send('service_actze3q', 'template_g1aliap', templateParams, {
        publicKey: 'lCc_l-5B-eiXyuh6V',
        // Der private Key wird NICHT im Frontend verwendet
      });

      setFormSubmitted(true);
      setFormError(null);
    } catch (err: any) {
      console.error('E-Mail-Versand fehlgeschlagen:', err);
      const reason = err?.text || err?.message || 'Unbekannter Fehler';
      setFormError(`Der Versand ist fehlgeschlagen. ${reason}`);
      return;
    }
    finally {
      setIsSending(false);
    }
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      topic: 'Allgemeine Anfrage',
      message: '',
      // lp entfernt
      captcha: '',
      dataProtection: false
    });
  };
  
  return (
    <div className="contact-page">
      {/* Header Section */}
      <header className="contact-header">
        <div className="container">
          <h1>Kontakt</h1>
          <p>
            Haben Sie Fragen oder möchten Sie ein Projekt mit uns besprechen? 
            Kontaktieren Sie uns gerne - wir freuen uns auf Ihre Anfrage!
          </p>
        </div>
      </header>
      
      <div className="contact-content">
        <div className="container">
          {/* Contact Info Section */}
          <div className="contact-info-container">
            <div className="contact-info">
              <div>
                <div className="contact-info-icon">
                  <FontAwesomeIcon icon={faPhone} />
                </div>
                <h3 className="contact-info-title">Telefon</h3>
                <p className="contact-info-text">
                  Rufen Sie uns an für eine direkte Beratung oder um einen Termin zu vereinbaren.
                </p>
              </div>
              <div>
                <a href="tel:+4990909590010" className="contact-info-link">
                  +49 (0) 90 90 - 95 900-10
                </a>
              </div>
            </div>
            
            <div className="contact-info">
              <div>
                <div className="contact-info-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <h3 className="contact-info-title">E-Mail</h3>
                <p className="contact-info-text">
                  Senden Sie uns eine E-Mail mit Ihrem Anliegen oder Ihrer Anfrage.
                </p>
              </div>
              <div>
                <a href="mailto:info@neubaur-gmbh.de" className="contact-info-link">
                  info@neubaur-gmbh.de
                </a>
              </div>
            </div>
            
            <div className="contact-info">
              <div>
                <div className="contact-info-icon">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <h3 className="contact-info-title">Öffnungszeiten</h3>
                <p className="contact-info-text">
                  Montag - Freitag: 8:00 - 17:00 Uhr<br />
                  Samstag & Sonntag: Geschlossen
                </p>
              </div>
              <div>
                <p className="contact-info-text contact-info-highlight">
                  Termine nach Vereinbarung
                </p>
              </div>
            </div>
          </div>
          
          {/* Contact Form Section */}
          <div className="row">
            <div className="col-lg-8 offset-lg-2">
              <div className="contact-form-container">
                <h3 className="contact-form-title">Kontaktformular</h3>
                
                {formSubmitted && (
                  <div className="alert alert-success" role="alert">
                    Vielen Dank für Ihre Nachricht! Wir werden uns so schnell wie möglich bei Ihnen melden.
                  </div>
                )}
                
                {formError && (
                  <div className="alert alert-danger" role="alert">
                    {formError}
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label htmlFor="name" className="form-label">Name *</label>
                        <input type="text" className="form-control" id="name" name="name" value={formData.name} onChange={handleChange} required />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label htmlFor="email" className="form-label">E-Mail *</label>
                        <input type="email" className="form-control" id="email" name="email" value={formData.email} onChange={handleChange} required />
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label htmlFor="phone" className="form-label">Telefon</label>
                        <input
                          type="tel"
                          className="form-control"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    
                    <div className="form-col">
                      <div className="form-group">
                        <label htmlFor="topic" className="form-label">Thema *</label>
                        <select id="topic" name="topic" className="form-control" value={formData.topic} onChange={handleChange} required>
                          {topics.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* LP-Auswahl entfernt */}
                  
                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Nachricht *</label>
                    <textarea
                      className="form-control"
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label htmlFor="captcha" className="form-label">Testfrage: Was ist 4 + 3? *</label>
                        <input type="text" className="form-control" id="captcha" name="captcha" value={formData.captcha} onChange={handleChange} required />
                      </div>
                    </div>
                  </div>

                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="dataProtection"
                      name="dataProtection"
                      checked={formData.dataProtection}
                      onChange={handleCheckboxChange}
                      required
                    />
                    <label className="form-check-label" htmlFor="dataProtection">
                      Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a> und die <a href="/agb" target="_blank" rel="noopener noreferrer">AGB</a> gelesen und stimme der Verarbeitung meiner Daten zu. *
                    </label>
                  </div>
                  
                  <div className="form-buttons">
                    <button type="submit" className="btn btn-primary btn-submit" disabled={isSending}>
                      {isSending ? 'Wird gesendet…' : 'Nachricht senden'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          
          {/* Locations Section */}
          <section className="location-section">
            <h2 className="location-title">Unsere Standorte</h2>
            
            <div className="locations-grid">
              <div className="location-card">
                <div className="location-map">
                  <ConsentEmbed
                    category="externalMedia"
                    providerName="Google Maps"
                    description="Beim Laden der Karte werden Daten (insbesondere Ihre IP-Adresse) an Google in Irland und ggf. die USA übertragen."
                    privacyPolicyUrl="https://policies.google.com/privacy"
                    ariaLabel="Karte des Standorts Rain am Lech"
                  >
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2650.8734969041985!2d10.90376031562195!3d48.38167297924341!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479ef2f3b38dfda1%3A0x41e48add78b5a30!2sNelkenweg%206A%2C%2086641%20Rain!5e0!3m2!1sde!2sde!4v1629789456789!5m2!1sde!2sde"
                      allowFullScreen
                      loading="lazy"
                      title="Standort Rain am Lech"
                    ></iframe>
                  </ConsentEmbed>
                </div>
                <div className="location-details">
                  <h3 className="location-name">
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> Rain am Lech (Hauptstandort)
                  </h3>
                  <p className="location-address">
                    Nelkenweg 6a<br />
                    86641 Rain am Lech
                  </p>
                </div>
              </div>
              

            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default KontaktPage;